<script lang="ts" setup>
  import { useCart, useGoogleTagManager, selectItemEvent } from '@storefront/storefront';
  import { useHttpClient, useShared, useRoute } from '@othercommerce/foundation';
  import type { OrderItemWithStock } from '@storefront/resources';

  const open = defineModel('open', { type: Boolean, required: false, default: false });

  const route = useRoute();
  const http = useHttpClient();
  const { cart, items } = useCart();
  const shared = useShared();
  const gtm = useGoogleTagManager();

  function onGoToCart() {
    open.value = false;
    http.get(route('web.cart.show'));
  }

  function onListCardClick(item: OrderItemWithStock) {
    open.value = false;
    gtm.push(selectItemEvent(item, shared.value.activeCurrencyCode));
    http.get(route('web.product.show', { slug: item.variantSlug }));
  }

</script>
<template>
  <UiDrawer md v-model:open="open" id="drawer-cart-drawer" class="drawer-cart" aria-labelledby="drawer-cart-drawer-title">
    <SfDrawerHeader headerType="span" headingStyle v-model:open="open" headingId="drawer-cart-drawer-title">
      {{ $t('SHOP.CART.HEADER.CART') }}
    </SfDrawerHeader>

    <div class="drawer-cart__products">
      <template v-if="items.length > 0">
        <SfDrawerCartItem v-for="item in items" :item :key="item.id" @click="onListCardClick(item)" @keydown.enter="onListCardClick(item)" />
      </template>
      <template v-else>
        <SfDataPlaceholder>
          {{ $t('SHOP.CART.MESSAGE.NO_PRODUCTS_IN_CART') }}
        </SfDataPlaceholder>
      </template>
    </div>
    <OrderSummary :order="cart" class="drawer-cart__summary">
      <SfButton primary @click="onGoToCart">
        {{ $t('SHOP.CART.ACTION.GO_TO_CART') }}
      </SfButton>
    </OrderSummary>
  </UiDrawer>
</template>
<style>
  :root {
    --sf-cart-drawer-box-shadow: var(--secondary-box-shadow-100);
    --sf-cart-drawer-border: none;
    --sf-cart-drawer-border-width: 0;
    --sf-cart-drawer-border-style: none;

    --sf-cart-drawer-header-background: white;
    --sf-cart-drawer-header-padding-y: 1rem;
    --sf-cart-drawer-header-padding-x: 1rem;

    --sf-cart-drawer-padding-x: 0;
    --sf-cart-drawer-padding-y: 0;
    --sf-cart-drawer-gap-y: 0;
  }
</style>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .drawer-cart {
    --sf-drawer-header-background: var(--sf-cart-drawer-header-background);
    --sf-drawer-header-padding-y: var(--sf-cart-drawer-header-padding-y);
    --sf-drawer-header-padding-x: var(--sf-cart-drawer-header-padding-x);

    :deep( .drawer__content) {
      padding: 0;
      gap: 0;
    }

    .drawer__header {
      position: sticky;
      top: 0;
      box-shadow: var(--sf-cart-drawer-box-shadow);
      border-bottom-color: var(--sf-cart-drawer-border);
      border-bottom-width: var(--sf-cart-drawer-border-width);
      border-bottom-style: var(--sf-cart-drawer-border-style);
      z-index: 9;
    }

    .order-cart-summary {
      position: sticky;
      bottom: 0;
      padding: 1rem;
      background-color: var(--color-white);
      z-index: 9;
      box-shadow: var(--sf-cart-drawer-box-shadow);
      border-top-color: var(--sf-cart-drawer-border);
      border-top-width: var(--sf-cart-drawer-border-width);
      border-top-style: var(--sf-cart-drawer-border-style);
      border-radius: 0 0 var(--ui-drawer-content-border-radius) var(--ui-drawer-content-border-radius);
    }

    &__products {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      padding: 1rem;
      flex-grow: 1;
    }

    @include media.query(lg) {
      &__products {
        gap: 0.5rem;
        padding: 1rem 0.5rem;
        --sf-list-card-gap: 0.5rem
      }

      .order-cart-summary {
        padding-bottom: 1.75rem;
      }
    }
  }
</style>
